import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../shared/button";
import { AuthButtons } from "./AuthButtons";
import { useAuthState } from "@/hooks/useAuthState";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { ROUTES } from "@/config/routes";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { useContext } from "react";
import { AssessmentContext } from "@/contexts/AssessmentContext";

export const Header = () => {
  const { showOpportunityAssessment, showReadinessAssessment } = useFeatureFlags();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthState();
  // Use optional context to avoid errors when AssessmentProvider is not available
  const assessmentContext = useContext(AssessmentContext);
  const assessmentLoading = assessmentContext?.isLoading || false;
  const [hasSavedAssessment, setHasSavedAssessment] = useState(false);
  const isReadinessActive = location.pathname.startsWith('/readiness');
  const isOpportunityActive = location.pathname.startsWith('/assessment');
  const isBenchmarkingActive = location.pathname.startsWith('/benchmarking');

  useEffect(() => {
    const checkSavedAssessment = async () => {
      if (user) {
        console.log('Checking for saved assessments for user:', user.id);
        const { data: assessments, error } = await supabase
          .from('assessments')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!error && assessments && assessments.length > 0) {
          console.log('Found saved assessment:', assessments[0].id);
          setHasSavedAssessment(true);
        } else {
          console.log('No saved assessments found');
          setHasSavedAssessment(false);
        }
      }
    };

    // Always check for saved assessments, but use the assessment loading state if available
    checkSavedAssessment();
  }, [user]);

  const handleOpportunityClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('Opportunity link clicked, hasSavedAssessment:', hasSavedAssessment);
    
    if (!user) {
      console.log('No user logged in, navigating to wizard');
      navigate('/assessment/wizard');
      return;
    }

    // If assessment context is available and loading, don't make a decision yet
    if (assessmentContext && assessmentLoading) {
      console.log('Assessment still loading, showing loading state');
      // Show loading state or wait
      return;
    }

    // Always navigate to wizard when there's a saved assessment
    // This ensures we're consistent with the URL pattern
    if (hasSavedAssessment) {
      console.log('User has saved assessment, navigating to wizard with step=2');
      navigate('/assessment/wizard');
    } else {
      console.log('No saved assessment found, navigating to wizard');
      navigate('/assessment/wizard');
    }
  };

  return (
    <>
      <header className="bg-white w-full overflow-hidden py-[21px] max-md:max-w-full font-inter">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
          <div className="flex w-full items-center justify-between gap-4">
            <Link to="/" className="flex items-center">
              <img
                src="/lovable-uploads/0dcf3837-946c-450b-b2f4-874d397550a7.png"
                alt="Company Logo"
                className="aspect-[10] object-contain w-[309px] max-w-[200px] sm:max-w-[250px] md:max-w-[309px] shrink-0"
              />
            </Link>
            <nav className="flex items-center">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-base font-normal leading-normal">
                <Button
                  asChild
                  variant="ghost"
                  className="text-[#CA0079] gap-[11px] whitespace-nowrap px-4 sm:px-[31px] py-[10px] sm:py-[13px] rounded-[10px] hover:bg-[#CA0079] hover:text-white transition-colors text-sm sm:text-base"
                >
                  <a href="https://www.govstream.ai/#request-a-demo" target="_blank" rel="noopener noreferrer">
                    Get Support
                  </a>
                </Button>
                <AuthButtons />
              </div>
            </nav>
          </div>
        </div>
      </header>

      <nav className="w-full text-[rgba(33,33,33,1)] mt-[15px] pb-5 max-md:mt-10 font-inter">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
          <h1 className="text-3xl font-medium max-md:max-w-full">
            <Link to={ROUTES.HOME} className="">PermitLab: Your Toolkit for AI-Enabled Permitting</Link>
          </h1>
          <div className="flex w-full items-center gap-[40px_42px] text-base font-normal leading-normal flex-wrap mt-[21px] max-md:max-w-full max-md:flex-col max-md:items-start max-md:gap-4">
            <Link
              to={ROUTES.PERMIT_BENCHMARKING.ROOT}
              className={`relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors ${
                isBenchmarkingActive
                  ? 'text-[#CA0079] font-medium after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079]'
                  : 'text-[#212121] hover:text-[#CA0079] after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079] after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100'
              }`}
            >
              Permit Benchmarking
            </Link>

            {showOpportunityAssessment ? (
              <button
                onClick={handleOpportunityClick}
                className={`relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors ${
                  isOpportunityActive
                    ? 'text-[#CA0079] font-medium after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079]'
                    : 'text-[#212121] hover:text-[#CA0079] after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079] after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100'
                }`}
              >
                Opportunity Assessment
              </button>
            ) : (
              <button
                disabled
                className="relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left text-gray-400 cursor-not-allowed"
              >
                Opportunity Assessment
              </button>
            )}

            {/* Readiness Assessment */}
            {showReadinessAssessment ? (
              <Link
                to={ROUTES.READINESS_ASSESSMENT.ROOT}
                className={`relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors ${
                  isReadinessActive
                    ? "text-[#CA0079] font-medium after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079]"
                    : "text-[#212121] hover:text-[#CA0079] after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079] after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100"
                }`}
              >
                Readiness Assessment
              </Link>
            ) : (
              null
            )}
          </div>
        </div>
      </nav>
    </>
  );
};